import { useCallback, useEffect, useRef, useState } from 'react';

type UseFillScrollableViewportProps = {
  /** Id of the element that scrolls — the same one the infinite scroll listens to. */
  scrollableTargetId: string;
  /** Whether the server has another page to give. */
  hasMore: boolean;
  /** True while a page is on its way, so a fetch is never asked for twice. */
  loading: boolean;
  /** How many items are rendered right now. */
  dataLength: number;
  /** Asks for the next page — the same callback the infinite scroll is given. */
  next: () => void;
};

/**
 * Keeps asking for the next page while the list is too short to scroll.
 *
 * An infinite scroll only ever hears about a scroll event, so a list that fits its container never
 * loads a second page: on a wide screen the first ten deployments fill two rows of a grid that has
 * room for six, there is nothing to scroll, and the listing sits there looking complete. The same
 * happens when the window grows.
 *
 * Once the content does overflow this stays out of the way and the scroll takes over.
 */
export const useFillScrollableViewport = ({
  scrollableTargetId,
  hasMore,
  loading,
  dataLength,
  next
}: UseFillScrollableViewportProps) => {
  // Bumped by the resize observer, so growing the window re-runs the check below.
  const [resizeCount, setResizeCount] = useState(0);
  // The length a page was last asked for at. Without it a server that keeps answering "there is
  // more" while returning nothing new would be asked again on every render.
  const askedAtLength = useRef(-1);

  const onResize = useCallback(() => {
    askedAtLength.current = -1;
    setResizeCount(count => count + 1);
  }, []);

  useEffect(() => {
    const target = document.getElementById(scrollableTargetId);
    if (!target || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(onResize);
    observer.observe(target);

    return () => observer.disconnect();
  }, [onResize, scrollableTargetId]);

  useEffect(() => {
    if (loading || !hasMore || askedAtLength.current === dataLength) {
      return;
    }

    const target = document.getElementById(scrollableTargetId);
    // A single pixel of slack: the two heights are rounded, and an exact match already means the
    // content ends where the box does.
    if (!target || target.scrollHeight > target.clientHeight + 1) {
      return;
    }

    askedAtLength.current = dataLength;
    next();
  }, [dataLength, hasMore, loading, next, resizeCount, scrollableTargetId]);
};

export default useFillScrollableViewport;
